import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'common/components';
import { ApiStatus, ButtonLabel, EntityActions } from 'common/enums';
import {
    LINK_CONFIRMATION_MODAL_TEXT,
    LINK_CONFIRMATION_MODAL_TITLE,
    LINK_MODAL_TEXT,
    LINK_MODAL_TITLE
} from 'common/config';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import SingleSelectMasterData from '../single-select-master-data/SingleSelectMasterData';

import './LinkIndicator.scss';
import { masterDataActions } from 'modules/master-data/redux';
import { useSelector } from 'react-redux';
import { RootState } from 'common/redux/core/root.reducer';
import { getLinkIndicatorStatus } from 'modules/master-data/redux/masterData.selector';
import { hasValue } from 'common/utils/data';

type LinkIndicatorProps = {
    isVisible: boolean;
    hierarchicalData: MasterDataEntity[];
    indicatorToLink?: EntityVM;
    closePopupHandler: () => void;
    updateSelectedRadioButton: (entity: EntityVM) => void;
    toggleExpansion: (payload: EntityVM) => void;
    parentSubCategory?: EntityVM;
    fetchNextHierachySet: () => void;
};

export default function LinkIndicatorPopup({
    isVisible,
    hierarchicalData,
    indicatorToLink,
    closePopupHandler,
    updateSelectedRadioButton,
    toggleExpansion,
    parentSubCategory,
    fetchNextHierachySet
}: LinkIndicatorProps) {
    const dispatch = useDispatch();

    const [isShowLinkConfirm, setIsShowLinkConfirm] = useState(false);
    const [parentEntity, setParentEntity] = useState<EntityVM>();

    const linkIndicatorStatus = useSelector((state: RootState) => getLinkIndicatorStatus(state));

    useEffect(() => {
        if (
            linkIndicatorStatus.status === ApiStatus.SUCCESS ||
            linkIndicatorStatus.status === ApiStatus.ERROR
        ) {
            setIsShowLinkConfirm(false);
            closePopupHandler();
            dispatch(masterDataActions.resetState());
        }
    }, [closePopupHandler, dispatch, linkIndicatorStatus.status]);

    const onSubmitHandler = () => {
        setIsShowLinkConfirm(true);
    };

    const onSubmitConfirm = () => {
        if (parentEntity && indicatorToLink)
            dispatch(
                masterDataActions.linkIndicator({
                    indicatorId: indicatorToLink?.id,
                    sourceHierarchyId: indicatorToLink?.hierarchyId,
                    targetHierarchyId: parentEntity?.hierarchyId
                })
            );
    };

    const onRadioButtonSelect = (data: EntityVM) => {
        setParentEntity(data);
        updateSelectedRadioButton(data);
    };

    return (
        <>
            <Modal
                show={isVisible}
                onClose={closePopupHandler}
                style={{ width: '50vw', height: '90vh' }}
                className="link-indicator-popup">
                <ModalHeader>{LINK_MODAL_TITLE}</ModalHeader>
                <ModalBody>
                    <div style={{ height: '45vh' }}>
                        <div style={{ height: '100%' }}>
                            <Label text={LINK_MODAL_TEXT}></Label>
                            <SingleSelectMasterData
                                hideSelectionOnCategory={true}
                                hierarchicalData={hierarchicalData}
                                updateSelectedRadioButton={onRadioButtonSelect}
                                toggleExpansion={toggleExpansion}
                                hideChildIndicatorsOnly={true}
                                disbleEntity={parentSubCategory}
                                preventSelectionOfSubCategoriesHavingSubCategories={true}
                                fetchNextHierachySet={fetchNextHierachySet}
                            />
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="footer-container">
                        <div className="footer-container__right">
                            <Button
                                size="medium"
                                type="button"
                                onClick={onSubmitHandler}
                                disabled={!hasValue(parentEntity?.hierarchyId)}>
                                {EntityActions.Link}
                            </Button>
                            <Button
                                size="medium"
                                variant="secondary"
                                type="button"
                                onClick={closePopupHandler}>
                                {EntityActions.Cancel}
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
            {isShowLinkConfirm && (
                <MessageModal
                    headerName={LINK_CONFIRMATION_MODAL_TITLE}
                    message={LINK_CONFIRMATION_MODAL_TEXT}
                    isVisible={isShowLinkConfirm}
                    isSecondaryButtonVisible={true}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={onSubmitConfirm}
                    onSecondaryAction={() => setIsShowLinkConfirm(false)}
                />
            )}
        </>
    );
}
